import _extends from "@babel/runtime/helpers/extends";
var _toolbar, _h, _h2, _h3, _h4, _h5;
import { createTheme } from '@mui/material/styles';
import { createBreakpoints } from '@mui/system';
import colorPalette from '../common/palette';
import { colorBackgroundAlertDefault, colorBackgroundAlertError, colorBackgroundAlertNeutral, colorBackgroundAlertSuccess, colorBackgroundAlertWarning, colorBrandCtaActive, colorBrandCtaBase, colorBrandCtaHover, colorBrandNeutral100, colorBrandNeutral200, colorBrandNeutral250, colorBrandNeutral300, colorBrandNeutral400, colorBrandNeutral500, colorBrandNeutral600, colorFillAlertError, colorFillAlertNeutral, colorFillAlertSuccess, colorFillAlertWarning, colorTextAlertDefault, colorTextAlertError, colorTextAlertNeutral, colorTextAlertSuccess, colorTextAlertWarning, fontSizeHeader1, fontSizeHeader1Small, fontSizeHeader2, fontSizeHeader2Small, fontSizeHeader3, fontSizeHeader3Small, fontSizeHeader4, fontSizeHeader4Small, fontSizeHeader5, fontSizeHeader5Small, fontSizeHeader6, fontSizeLarge, fontSizeSmall, fontSizeDefault, fontFamilyDefault, fontFamilyHeader, fontFamilyCondensed, fontWeightLight, fontWeightNormal, fontWeightBold, lineHeightDefault, sizingXxLarge1, zIndexTextFieldLabel, zIndexDrawer, zIndexDropdown, zIndexHeaderBarDropdown, zIndexMegaMenu, zIndexInlineAlert, zIndexPageAlert, zIndexActionMenu, zIndexHeaderBar, zIndexModal, zIndexBackdrop } from '@ellucian/react-design-system/core/styles/tokens';

// Breakpoints must be defined in ascending order of viewport size. These breakpoints are used to calculate the size of the viewport in useWidth() hook.
// If values are "NOT" in ascending order then useWidth() hook will fail to return correct value.
var breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 480,
    md: 768,
    lg: 992,
    xl: 1920
  }
});

// All state names available in MUI v5
// https://mui.com/material-ui/customization/how-to-customize/#state-classes

var lightThemeOptions = {
  mode: 'light',
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true // Disable ripple effect globally on components
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: colorBrandNeutral500,
          '&.Mui-disabled:hover': {
            cursor: 'not-allowed'
          },
          '&.Mui-error': {
            color: colorBrandNeutral500
          },
          '&.Mui-focused': {
            color: colorBrandNeutral500
          }
        },
        asterisk: {
          '&.Mui-error': {
            color: colorBrandNeutral500
          }
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        formControl: {
          'label + &': {
            marginTop: 0,
            width: '100%'
          }
        }
      }
    },
    // Hover and selected states are same across
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: colorBrandNeutral100
          },
          '&.Mui-selected': {
            color: colorBrandNeutral100
          }
        }
      }
    }
  },
  breakpoints: breakpoints,
  mixins: {
    toolbar: (_toolbar = {
      minHeight: sizingXxLarge1
    }, _toolbar[breakpoints.up('xs') + " and (orientation: landscape)"] = {
      minHeight: sizingXxLarge1
    }, _toolbar[breakpoints.up('sm')] = {
      minHeight: sizingXxLarge1
    }, _toolbar)
  },
  palette: _extends({
    action: {
      active: colorBrandCtaActive,
      // cta blue 800
      hover: colorBrandCtaHover,
      // cta blue 700
      base: colorBrandCtaBase,
      // cta blue 600
      disabled: colorBrandNeutral400
    },
    grey: {
      100: colorBrandNeutral100,
      200: colorBrandNeutral200,
      250: colorBrandNeutral250,
      300: colorBrandNeutral300,
      400: colorBrandNeutral400,
      500: colorBrandNeutral500,
      600: colorBrandNeutral600
    },
    // new status color overrides
    default: {
      main: colorBackgroundAlertDefault,
      light: '#E9E9E9',
      // TODO: look into
      dark: '#D9D9D9',
      // TODO: look into
      contrastText: colorTextAlertDefault
    },
    error: {
      main: colorBackgroundAlertError,
      light: colorFillAlertError,
      dark: colorFillAlertError,
      contrastText: colorTextAlertError
    },
    success: {
      main: colorBackgroundAlertSuccess,
      light: colorFillAlertSuccess,
      dark: colorFillAlertSuccess,
      contrastText: colorTextAlertSuccess
    },
    info: {
      main: colorBackgroundAlertNeutral,
      light: colorFillAlertNeutral,
      dark: colorFillAlertNeutral,
      contrastText: colorTextAlertNeutral
    },
    warning: {
      main: colorBackgroundAlertWarning,
      light: colorFillAlertWarning,
      dark: colorFillAlertWarning,
      contrastText: colorTextAlertWarning
    }
  }, colorPalette),
  spacing: 4,
  typography: {
    fontFamily: fontFamilyDefault,
    fontSize: 14,
    // fontSizeDefault, MUI converts to rem for us
    fontWeightLight: fontWeightLight,
    fontWeightRegular: fontWeightNormal,
    fontWeightBold: fontWeightBold,
    h1: (_h = {
      fontFamily: fontFamilyHeader,
      fontSize: fontSizeHeader1,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em',
      letterSpacing: '0.03125rem'
    }, _h[breakpoints.down('md')] = {
      fontSize: fontSizeHeader1Small
    }, _h),
    h2: (_h2 = {
      fontFamily: fontFamilyHeader,
      fontSize: fontSizeHeader2,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em',
      letterSpacing: '0.03125rem'
    }, _h2[breakpoints.down('md')] = {
      fontSize: fontSizeHeader2Small
    }, _h2),
    h3: (_h3 = {
      fontFamily: fontFamilyHeader,
      fontSize: fontSizeHeader3,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      letterSpacing: '0.03125rem',
      wordSpacing: '0.16em'
    }, _h3[breakpoints.down('md')] = {
      fontSize: fontSizeHeader3Small
    }, _h3),
    h4: (_h4 = {
      fontFamily: fontFamilyDefault,
      fontSize: fontSizeHeader4,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em'
    }, _h4[breakpoints.down('md')] = {
      fontSize: fontSizeHeader4Small
    }, _h4),
    h5: (_h5 = {
      fontFamily: fontFamilyDefault,
      fontWeight: fontWeightBold,
      fontSize: fontSizeHeader5,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em'
    }, _h5[breakpoints.down('md')] = {
      fontSize: fontSizeHeader5Small
    }, _h5),
    h6: {
      fontFamily: fontFamilyDefault,
      fontSize: fontSizeHeader6,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em'
    },
    subtitle1: {
      // set to h5 styles in case anyone uses this
      fontFamily: fontFamilyDefault,
      fontSize: fontSizeHeader5,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em'
    },
    subtitle2: {
      // set to h6 styles in case anyone uses this
      fontFamily: fontFamilyDefault,
      fontSize: fontSizeHeader6,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em'
    },
    body1: {
      fontFamily: fontFamilyDefault,
      fontSize: fontSizeLarge,
      fontWeight: fontWeightNormal,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em'
    },
    body2: {
      fontFamily: fontFamilyDefault,
      fontSize: fontSizeDefault,
      fontWeight: fontWeightNormal,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em'
    },
    body3: {
      fontFamily: fontFamilyDefault,
      fontSize: fontSizeSmall,
      fontWeight: fontWeightNormal,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em',
      letterSpacing: 'initial',
      textTransform: 'initial'
    },
    button: {
      fontFamily: fontFamilyCondensed,
      fontSize: fontSizeLarge,
      fontWeight: fontWeightBold,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em',
      letterSpacing: '0.0625rem',
      textTransform: 'uppercase'
    },
    caption: {
      fontFamily: fontFamilyCondensed,
      fontSize: fontSizeDefault,
      fontWeight: fontWeightNormal,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em',
      letterSpacing: '0.0625rem',
      textTransform: 'uppercase'
    },
    overline: {
      // set to caption styles in case anyone uses this
      fontFamily: fontFamilyCondensed,
      fontSize: fontSizeDefault,
      fontWeight: fontWeightNormal,
      lineHeight: lineHeightDefault,
      wordSpacing: '0.16em',
      letterSpacing: '0.0625rem',
      textTransform: 'uppercase'
    }
  },
  zIndex: {
    textFieldLabel: zIndexTextFieldLabel,
    dropdown: zIndexDropdown,
    headerBarDropdown: zIndexHeaderBarDropdown,
    actionMenu: zIndexActionMenu,
    megaMenu: zIndexMegaMenu,
    inlineAlert: zIndexInlineAlert,
    pageAlert: zIndexPageAlert,
    headerBar: zIndexHeaderBar,
    modal: zIndexModal,
    drawer: zIndexDrawer,
    backdrop: zIndexBackdrop
  }
};
var lightTheme = createTheme(lightThemeOptions);
export { lightTheme as default, lightThemeOptions };